define(['bignumber.js', 'modules/common/controls/input', 'appcontext'], (BigNumber, Input, appContext) => {

    function defaults () {
        const d = appContext.numeralDelimeters().get();
        return {aSep: d.thousands, aDec: d.decimal, mRound: 'B'};
    }

    const types = {
        id (options) {
            options = _.defaults(options || {}, {min: '0', max: '99999999999999'});
            return _.extend(defaults(), {vMin: options.min, vMax: options.max, aSep: '', nullable: true});
        },
        integer (options) {
            options = _.defaults(options || {}, {min: '0', max: '99999999999999', negative: false});
            if (options.negative) {
                options.min = `-${options.max}`;
            }
            return _.extend(defaults(), {vMin: options.min, vMax: options.max});
        },
        percent (options) {
            options = _.defaults(options || {}, {min: '0.00000000',
                max: '100.00000000',
                negative: false,
                symbol: true});
            if (options.negative) {
                options.min = `-${options.max}`;
            }
            const format = _.extend(defaults(), {vMin: options.min, vMax: options.max, aPad: false});
            if (options.symbol) {
                _.extend(format, {aSign: '%', pSign: 's'});
            }
            return format;
        },
        decimal (options) {
            options = _.defaults(options || {}, {min: '0.00000000', max: '99999999999999.00000000', negative: false});
            if (options.negative) {
                options.min = `-${options.max}`;
            }
            return _.extend(defaults(), {vMin: options.min, vMax: options.max, aPad: false});
        },
        coefficient (options) {
            options = _.defaults(options || {}, {min: '0.00000000', max: '9999.00000000', negative: false});
            if (options.negative) {
                options.min = `-${options.max}`;
            }
            return _.extend(defaults(), {vMin: options.min, vMax: options.max, aPad: false});
        },
        currency (options) {
            options = _.defaults(options || {}, {min: '0.00',
                max: '99999999999999.00',
                negative: false,
                symbol: true,
                vat: true,
                vatInversed: false});
            if (options.negative) {
                options.min = `-${options.max}`;
            }
            const format = _.extend(defaults(), {vMin: options.min,
                vMax: options.max,
                vat: options.vat,
                vatInversed: options.vatInversed});
            if (options.symbol) {
                const symbol = appContext.currencySymbol(appContext.accountCurrency());
                _.extend(format, {aSign: symbol ? ' {0}'.format(symbol) : '', pSign: 's'});
            }
            return format;
        },
        currencyrate (options) {
            options = _.defaults(options || {}, {min: '0.00000000',
                max: '99999999999999.00000000',
                symbol: true,
                aPad: false});
            const format = _.extend(defaults(), {vMin: options.min, vMax: options.max, aPad: options.aPad});
            if (options.symbol) {
                const symbol = appContext.currencySymbol(appContext.accountCurrency());
                _.extend(format, {aSign: symbol ? ' {0}'.format(symbol) : '', pSign: 's'});
            }
            return format;
        }
    };

    const NumericInput = Input.extend({

        binders: _.extend({}, Input.prototype.binders, {
            value: {
                bind (view, publish) {
                    this.callback = function () {
                        publish(view.get());
                    };
                    view.$el.on('change', this.callback);
                },
                unbind (view) {
                    view.$el.off('change', this.callback);
                },
                routine (view, value) {
                    view.set(value);
                }
            },
            vatDate (view, value) {
                view.setVatDate(value);
            },
            vatValue (view, value) {
                view.setVatValue(value);
            }
        }),

        defaults: _.extend({}, Input.prototype.defaults, {type: 'integer'}),

        initialize () {
            Input.prototype.initialize.apply(this, arguments);

            if (_.isString(this.options.type)) {
                this.options.type = types[this.options.type](this.options);
            }

            if (this.options.type.vat) {
                this.listenTo(appContext, 'changeWithVat', this.changeWithVat);
            }
        },

        render () {
            Input.prototype.render.apply(this, arguments);
            this.$el.autoNumeric('destroy').autoNumeric('init', this.options.type);
            return this;
        },

        get (dirty) {
            let value = this.$el.autoNumeric('get');

            // first get (set to model) resets vat value and we calculate it.
            if (!_.isUndefined(this.vatValue)) {
                this.vatValue = undefined;
            }

            if (this.options.type.vat && !dirty && appContext.withVat()) {
                value = new BigNumber(value || 0).div(appContext.vatDivider(this.vatDate)).
                    toFixed(2);
            }
            return value;
        },

        set (value, dirty) {
            this.value = value; // Save value for further usage in changeWithVat.
            value = new BigNumber(value);
            if (this.options.type.vat && !dirty && appContext.withVat()) {
                value = value.times(appContext.vatDivider(this.vatDate)).toFixed(2);
            }

            // if vat value is set use it in case of toggled VAT switcher.
            if (!_.isUndefined(this.vatValue) && appContext.withVat()) {
                value = this.vatValue;
            }

            this.$el.autoNumeric('set', value);
        },

        setVatDate (value) {
            this.vatDate = value;
        },

        setVatValue (value) {
            this.vatValue = value;
        },

        changeWithVat () {
            const vatDivider = appContext.vatDivider(this.vatDate);
            let value = new BigNumber(this.$el.autoNumeric('get') || 0);

            if (!_.isUndefined(this.vatValue)) {
                value = appContext.withVat() ? this.vatValue : this.value;
            } else {
                value = (appContext.withVat() ? value.times(vatDivider) : value.div(vatDivider)).toFixed(2);
            }

            this.$el.autoNumeric('set', value);
        }

    });

    NumericInput.types = types;
    return NumericInput;
});
